import { useCallback, useEffect, useRef, useState } from 'react'
import { readServerChartPreference, writeServerChartPreference } from '../lib/chart/chartPreferenceStore.js'

const scope = 'position-workspace'
function normalize(value) {
  if (value?.mode === 'manual' && Array.isArray(value.rowIds)) return { mode: 'manual', rowIds: [...new Set(value.rowIds.filter(id => typeof id === 'string' && id))] }
  if (value?.mode === 'column' && ['symbol', 'last', 'changePercent', 'extendedHours', 'quantity', 'pnl'].includes(value.sort?.field)) return { mode: 'column', sort: { field: value.sort.field, direction: value.sort.direction === 'desc' ? 'desc' : 'asc' }, ...(Array.isArray(value.rowIds) ? { rowIds: [...new Set(value.rowIds.filter(id => typeof id === 'string' && id))] } : {}) }
  return null
}
export function useAssetListRowOrder(userId, viewKey = 'lifecycle:ideas', label = 'Ideas') {
  const preferenceKey = viewKey === 'lifecycle:ideas' ? 'ideas-row-order' : `asset-list-row-order:${viewKey}`
  const identity = `${userId || ''}:${preferenceKey}`
  const [state, setState] = useState({ owner: null, value: null, ready: false, saving: false, error: null })
  const generation = useRef(0), pending = useRef(false), owner = useRef(identity)
  owner.current = identity
  const reload = useCallback(() => {
    const token = ++generation.current
    pending.current = false
    setState({ owner: identity, value: null, ready: false, saving: false, error: null })
    if (!userId) return
    readServerChartPreference({ userId, scope, preferenceKey, throwOnError: true }).then(value => {
      if (token === generation.current && owner.current === identity) setState({ owner: identity, value: normalize(value), ready: true, saving: false, error: null })
    }).catch(() => {
      if (token === generation.current && owner.current === identity) setState(current => ({ ...current, error: `Could not load ${label} order.` }))
    })
  }, [userId, identity, preferenceKey, label])
  useEffect(() => { reload(); return () => { generation.current += 1 } }, [reload])
  const save = useCallback(async value => {
    if (!userId || owner.current !== identity || state.owner !== identity || !state.ready || pending.current) return
    if (value?.mode === 'column' && !Array.isArray(value.rowIds) && Array.isArray(state.value?.rowIds)) value = { ...value, rowIds: state.value.rowIds }
    value = normalize(value)
    if (!value) return
    const token = generation.current, previous = state.value
    pending.current = true
    setState(current => ({ ...current, value, saving: true, error: null }))
    try {
      if (!await writeServerChartPreference({ userId, scope, preferenceKey, preferences: value })) throw Error('save failed')
      if (token === generation.current && owner.current === identity) setState(current => ({ ...current, saving: false }))
    } catch {
      if (token === generation.current && owner.current === identity) setState(current => ({ ...current, value: previous, saving: false, error: `Could not save ${label} order. Previous order restored; try again.` }))
    } finally { if (token === generation.current) pending.current = false }
  }, [userId, identity, preferenceKey, label, state.owner, state.ready, state.value])
  return { ...state, value: state.owner === identity ? state.value : null, ready: state.owner === identity && state.ready, save, reload }
}
